import { FC, useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "@/components/Snackbar";
import {
  useGetIntentTopicCollectionTreeQuery,
  useSearchIntentTopicsQuery,
} from "@/generated/taxonomy";
import { TAXONOMY_CLIENT } from "@/utils/consts";
import { getExpandedItemIds } from "@/components/filters/TreeSelect/utils";
import { useDebouncedValue } from "../../../../utils/useDebouncedValue";
import { MAX_INTENT_TOPICS } from "../../slice";
import {
  type CollectionInfo,
  useBuildTopicTree,
} from "../../hooks/useBuildTopicTree";
import {
  buildTreeViewItems,
  collectFullySelectedCollections,
  fromTopicNodeId,
  toCollectionNodeId,
} from "../../utils/topicTreeView";
import { TopicsSearchTab } from "../../sections/topics/TopicsSearchTab";
import { TopicsSelectedTab } from "../../sections/topics/TopicsSelectedTab";
import type { CachedTopic } from "@/types/intent";

const SEARCH_CONSTANTS = {
  MIN_FILTER_LENGTH: 3,
  DEBOUNCE_DELAY: 300,
};

export interface TopicsSearchFilterProps {
  selectedTopicIds: string[];
  topicsCache: CachedTopic[];
  onSelectionChange: (topicIds: string[], cache: CachedTopic[]) => void;
  maxTopics?: number;
}

export const TopicsSearchFilter: FC<TopicsSearchFilterProps> = ({
  selectedTopicIds,
  topicsCache,
  onSelectionChange,
  maxTopics = MAX_INTENT_TOPICS,
}) => {
  const { showWarning } = useSnackbar();

  const [topicsTab, setTopicsTab] = useState<"results" | "selected">("results");
  const [inputValue, setInputValue] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("");

  const trimmedInput = inputValue.trim();
  const debouncedTrimmedInput = useDebouncedValue(
    trimmedInput,
    SEARCH_CONSTANTS.DEBOUNCE_DELAY
  );
  const searchQuery =
    trimmedInput.length < SEARCH_CONSTANTS.MIN_FILTER_LENGTH
      ? ""
      : debouncedTrimmedInput;

  useEffect(() => {
    if (trimmedInput.length >= SEARCH_CONSTANTS.MIN_FILTER_LENGTH) {
      setTopicsTab("results");
    }
  }, [trimmedInput]);

  const shouldSearch = searchQuery.length >= SEARCH_CONSTANTS.MIN_FILTER_LENGTH;

  const { data: collectionTreeData } = useGetIntentTopicCollectionTreeQuery({
    context: { clientName: TAXONOMY_CLIENT },
    skip: !shouldSearch,
  });

  const collections = useMemo((): CollectionInfo[] => {
    if (!collectionTreeData?.intentTopicCollectionTree?.data) return [];
    return collectionTreeData.intentTopicCollectionTree.data
      .filter(
        (c): c is NonNullable<typeof c> =>
          c !== null && c.id !== null && c.displayName !== null
      )
      .map((c) => ({
        id: c.id as string,
        displayName: c.displayName as string,
        parentId: (c.parentId as string) ?? null,
      }));
  }, [collectionTreeData]);

  const { data: searchData, loading: searchLoading } =
    useSearchIntentTopicsQuery({
      variables: {
        displayName: searchQuery,
      },
      skip: !shouldSearch,
      context: { clientName: TAXONOMY_CLIENT },
    });

  const searchResults = useMemo((): CachedTopic[] => {
    if (!searchData?.intentTopic?.data) return [];
    return searchData.intentTopic.data
      .filter(
        (topic): topic is NonNullable<typeof topic> =>
          topic !== null && topic.id !== null && topic.displayName !== null
      )
      .map((topic) => ({
        id: topic.id as string,
        displayName: topic.displayName as string,
        category: topic.category ?? undefined,
        collectionTree: (topic.collectionTree ?? []).filter(
          (t): t is string => t !== null && t !== "0"
        ),
      }));
  }, [searchData]);

  const rootTopics = useMemo(
    () => searchResults.filter((topic) => topic.collectionTree.length === 0),
    [searchResults]
  );

  const topicsWithCollections = useMemo(
    () => searchResults.filter((topic) => topic.collectionTree.length > 0),
    [searchResults]
  );

  const searchTrees = useBuildTopicTree(topicsWithCollections, collections);

  const { items: searchItems, topicNodeMap } = useMemo(
    () => buildTreeViewItems(searchTrees, rootTopics),
    [searchTrees, rootTopics]
  );

  const searchExpandedItems = useMemo(
    () => getExpandedItemIds(searchItems),
    [searchItems]
  );

  const searchTopicIds = useMemo(
    () => new Set(searchResults.map((topic) => topic.id)),
    [searchResults]
  );

  const selectedTopicIdsInSearch = useMemo(
    () => selectedTopicIds.filter((id) => searchTopicIds.has(id)),
    [selectedTopicIds, searchTopicIds]
  );

  const selectedTopicSetInSearch = useMemo(
    () => new Set(selectedTopicIdsInSearch),
    [selectedTopicIdsInSearch]
  );

  const fullySelectedCollectionIds = useMemo(
    () =>
      collectFullySelectedCollections(searchTrees, selectedTopicSetInSearch),
    [searchTrees, selectedTopicSetInSearch]
  );

  const treeSelectedItems = useMemo(() => {
    const topicNodeIds = selectedTopicIdsInSearch.flatMap(
      (id) => topicNodeMap.get(id) ?? []
    );
    return [
      ...topicNodeIds,
      ...fullySelectedCollectionIds.map(toCollectionNodeId),
    ];
  }, [selectedTopicIdsInSearch, fullySelectedCollectionIds, topicNodeMap]);

  const selectedTopicsWithFallbackNames = useMemo(() => {
    const cacheById = new Map(topicsCache.map((t) => [t.id, t]));
    return selectedTopicIds.map((id) => {
      const cached = cacheById.get(id);
      if (cached) return cached;
      return {
        id,
        displayName: `Topic ${id.slice(0, 8)}...`,
        category: undefined,
        collectionTree: [],
      };
    });
  }, [selectedTopicIds, topicsCache]);

  const filteredSelectedTopics = useMemo(() => {
    const search = selectedFilter.trim().toLowerCase();
    if (!search) return selectedTopicsWithFallbackNames;
    return selectedTopicsWithFallbackNames.filter((topic) =>
      topic.displayName.toLowerCase().includes(search)
    );
  }, [selectedTopicsWithFallbackNames, selectedFilter]);

  const showTopicLimitSnackbar = useCallback(
    (options?: { added?: number; attempted?: number }) => {
      if (options?.added !== undefined && options?.attempted !== undefined) {
        showWarning(
          `Only ${options.added.toLocaleString()} of ${options.attempted.toLocaleString()} topics were added. Intent topics are limited to ${maxTopics.toLocaleString()}.`
        );
        return;
      }

      showWarning(
        `You can select up to ${maxTopics.toLocaleString()} intent topics. Remove some topics to add more.`
      );
    },
    [showWarning, maxTopics]
  );

  const handleTopicSelect = useCallback(
    (topic: CachedTopic, resetInput = true) => {
      if (selectedTopicIds.includes(topic.id)) return;
      if (selectedTopicIds.length >= maxTopics) {
        showTopicLimitSnackbar();
        return;
      }

      const newIds = [...selectedTopicIds, topic.id];
      const newCache = [...topicsCache, topic];
      onSelectionChange(newIds, newCache);

      if (resetInput) {
        setInputValue("");
      }
    },
    [
      selectedTopicIds,
      topicsCache,
      onSelectionChange,
      maxTopics,
      showTopicLimitSnackbar,
    ]
  );

  const handleTopicRemove = useCallback(
    (topicId: string) => {
      const newIds = selectedTopicIds.filter((id) => id !== topicId);
      const newCache = topicsCache.filter((t) => t.id !== topicId);
      onSelectionChange(newIds, newCache);
    },
    [selectedTopicIds, topicsCache, onSelectionChange]
  );

  const handleSearchSelectionChange = useCallback(
    (itemIds: string[]) => {
      const nextSearchTopicIds = itemIds
        .filter((id) => id.startsWith("topic:"))
        .map(fromTopicNodeId);
      const outsideSearchIds = selectedTopicIds.filter(
        (id) => !searchTopicIds.has(id)
      );
      const combinedIds = Array.from(
        new Set([...outsideSearchIds, ...nextSearchTopicIds])
      );

      if (
        combinedIds.length > maxTopics &&
        combinedIds.length > selectedTopicIds.length
      ) {
        showTopicLimitSnackbar();
        return;
      }

      const cacheById = new Map(topicsCache.map((t) => [t.id, t]));
      const searchById = new Map(searchResults.map((t) => [t.id, t]));
      const nextCache = combinedIds
        .map((id) => cacheById.get(id) ?? searchById.get(id))
        .filter((t): t is CachedTopic => !!t);

      onSelectionChange(combinedIds, nextCache);
    },
    [
      selectedTopicIds,
      topicsCache,
      searchResults,
      searchTopicIds,
      onSelectionChange,
      maxTopics,
      showTopicLimitSnackbar,
    ]
  );

  const handleClearTopics = useCallback(() => {
    onSelectionChange([], []);
  }, [onSelectionChange]);

  const handleClearInput = useCallback(() => {
    setInputValue("");
  }, []);

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={topicsTab}
          onChange={(_event, value: "results" | "selected") =>
            setTopicsTab(value)
          }
          variant="fullWidth"
          sx={{
            minHeight: 40,
            "& .MuiTab-root": {
              minHeight: 40,
              textTransform: "none",
            },
          }}
        >
          <Tab
            value="results"
            icon={<SearchIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Search"
          />
          <Tab
            value="selected"
            icon={
              <Chip
                label={
                  selectedTopicIds.length > 999
                    ? `${Math.floor(selectedTopicIds.length / 1000)}k`
                    : selectedTopicIds.length
                }
                size="small"
                sx={{
                  height: 20,
                  minWidth: 20,
                  fontSize: "0.75rem",
                  bgcolor:
                    selectedTopicIds.length > 0 ? "primary.main" : "grey.400",
                  color: "white",
                }}
              />
            }
            iconPosition="start"
            label="Selected"
          />
        </Tabs>
      </Box>

      {topicsTab === "results" && (
        <TopicsSearchTab
          inputValue={inputValue}
          onInputChange={setInputValue}
          onClearInput={handleClearInput}
          minChars={SEARCH_CONSTANTS.MIN_FILTER_LENGTH}
          shouldSearch={shouldSearch}
          searchQuery={searchQuery}
          searchLoading={searchLoading}
          resultsCount={searchResults.length}
          searchItems={searchItems}
          treeSelectedItems={treeSelectedItems}
          searchExpandedItems={searchExpandedItems}
          onSelectionChange={handleSearchSelectionChange}
          selectedTopicIds={selectedTopicIds}
          onAddSuggestedTopic={(topic) => handleTopicSelect(topic, false)}
        />
      )}

      {topicsTab === "selected" && (
        <TopicsSelectedTab
          selectedFilter={selectedFilter}
          onSelectedFilterChange={setSelectedFilter}
          onClearSelectedFilter={() => setSelectedFilter("")}
          totalSelectedCount={selectedTopicIds.length}
          filteredTopics={filteredSelectedTopics}
          onRemoveTopic={handleTopicRemove}
          showClearAll={selectedTopicIds.length > 0}
          onClearAll={handleClearTopics}
        />
      )}
    </Box>
  );
};
